<script setup lang="ts">
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Sparkles } from "lucide-vue-next";
  import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Spinner } from "@/components/ui/spinner";
  import { Select } from "@/components/episto-ui/select";
  import { useMAssetLibraryStore } from "@stores/mAssetLibrary";
  import ImageLibraryAssetThumbnail from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetThumbnail.vue";
  import type { MMediumUpload } from "@/types";

  interface ReviewRow {
    asset: MMediumUpload;
    tags: string[];
    savedTags: string[];
    isSuggesting: boolean;
  }

  const props = withDefaults(defineProps<{ open: boolean; acceptVideos?: boolean }>(), { acceptVideos: false });

  const accept = computed(() => (props.acceptVideos ? "image/*,video/*" : "image/*"));

  const emit = defineEmits<{ "update:open": [value: boolean] }>();

  const assetLibraryStore = useMAssetLibraryStore();
  const { allAssetTags } = storeToRefs(assetLibraryStore);
  const { uploadAssets, suggestTags, updateAsset, refreshManageData } = assetLibraryStore;

  const files = ref<File[]>([]);
  const preAppliedTags = ref<string[]>([]);
  const isUploading = ref(false);
  const isSaving = ref(false);
  const reviewRows = ref<ReviewRow[]>([]);

  const isReviewing = computed(() => reviewRows.value.length > 0);
  const isSuggesting = computed(() => reviewRows.value.some((row) => row.isSuggesting));
  const changedRows = computed(() =>
    reviewRows.value.filter((row) => row.tags.length && row.tags.join(",") !== row.savedTags.join(",")),
  );

  const tagOptions = computed(() => allAssetTags.value.map((tag) => ({ value: tag.name, label: tag.name })));

  const handleFilesChange = (event: Event) => {
    files.value = Array.from((event.target as HTMLInputElement).files || []);
  };

  const close = () => {
    files.value = [];
    preAppliedTags.value = [];
    reviewRows.value = [];
    emit("update:open", false);
  };

  // Suggestions arrive one image at a time; each row merges its own as soon as they land.
  const suggestFor = async (row: ReviewRow) => {
    const suggested = await suggestTags(row.asset.id);
    row.tags = [...new Set([...row.tags, ...suggested])];
    row.isSuggesting = false;
  };

  const upload = async () => {
    if (!files.value.length) return;
    isUploading.value = true;
    try {
      const uploaded = await uploadAssets(files.value, preAppliedTags.value);
      const images = uploaded.filter((asset) => asset.media_type === "image");
      if (!images.length) return close();

      reviewRows.value = images.map((asset) => ({
        asset,
        tags: [...(asset.asset_tags ?? [])],
        savedTags: [...(asset.asset_tags ?? [])],
        isSuggesting: true,
      }));
      reviewRows.value.forEach((row) => suggestFor(row));
    } finally {
      isUploading.value = false;
    }
  };

  const saveTags = async () => {
    isSaving.value = true;
    try {
      await Promise.all(
        changedRows.value.map((row) => updateAsset({ ...row.asset, asset_tags: row.tags }, { refresh: false })),
      );
      await refreshManageData();
      close();
    } finally {
      isSaving.value = false;
    }
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="(value) => (value ? emit('update:open', true) : close())">
    <DialogContent :class="isReviewing ? 'max-w-2xl' : 'max-w-md'">
      <DialogHeader>
        <DialogTitle>{{ isReviewing ? "Review the tags" : "Upload assets" }}</DialogTitle>
        <DialogDescription v-if="isReviewing">
          <Sparkles class="mr-1 inline h-3.5 w-3.5" />
          The AI looked at each image and added existing tags that fit. Adjust them, then save.
        </DialogDescription>
      </DialogHeader>

      <div
        v-if="isReviewing"
        class="max-h-[60vh] space-y-3 overflow-y-auto">
        <div
          v-for="row in reviewRows"
          :key="row.asset.id"
          class="flex items-start gap-3">
          <div class="h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-muted">
            <ImageLibraryAssetThumbnail :asset="row.asset" />
          </div>
          <div class="min-w-0 flex-1 space-y-1">
            <div class="flex items-center gap-2">
              <span class="truncate text-sm font-medium">{{ row.asset.name }}</span>
              <span
                v-if="row.isSuggesting"
                class="flex items-center gap-1 text-xs text-muted-foreground">
                <Spinner class="h-3 w-3" />
                Looking at the image…
              </span>
            </div>
            <Select
              v-model="row.tags"
              :options="tagOptions"
              multiple
              filterable
              creatable
              placeholder="Add tags" />
          </div>
        </div>
      </div>

      <div
        v-else
        class="space-y-4">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Files</label>
          <Input
            type="file"
            multiple
            :accept="accept"
            @change="handleFilesChange" />
          <p
            v-if="files.length"
            class="mt-1 text-xs text-muted-foreground">
            {{ files.length }} file(s) selected
          </p>
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Pre-apply tags</label>
          <Select
            v-model="preAppliedTags"
            :options="tagOptions"
            multiple
            filterable
            creatable
            placeholder="Pick tags to apply to the whole batch" />
        </div>
      </div>

      <DialogFooter v-if="isReviewing">
        <Button
          variant="ghost"
          :disabled="isSaving"
          @click="close">
          Skip
        </Button>
        <Button
          :disabled="isSaving || isSuggesting || !changedRows.length"
          @click="saveTags">
          {{ isSuggesting ? "Suggesting…" : `Save tags (${changedRows.length})` }}
        </Button>
      </DialogFooter>
      <DialogFooter v-else>
        <Button
          variant="ghost"
          :disabled="isUploading"
          @click="close">
          Cancel
        </Button>
        <Button
          :disabled="!files.length || isUploading"
          @click="upload">
          <span v-if="isUploading">Uploading…</span>
          <span v-else>Upload {{ files.length ? `(${files.length})` : "" }}</span>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
